import { Navigate, Route, Routes } from "react-router";
import { useTrackHistory } from "../shared/phone/history";
import { useNative } from "../shared/phone/media";
import { usePartnerAuth } from "./context/PartnerAuthContext";
import { PortalShell } from "./components/PortalShell";
import { LoginView } from "./views/LoginView";
import { SignupView } from "./views/SignupView";
import { ProgramsView } from "./views/ProgramsView";
import { ProgramsNative } from "./views/ProgramsNative";
import { OnboardingView } from "./views/OnboardingView";
import { HomeView } from "./views/HomeView";
import { ReferralsView } from "./views/ReferralsView";
import { AssetsView } from "./views/AssetsView";
import { PayoutsView } from "./views/PayoutsView";
import { ProfileView } from "./views/ProfileView";

/**
 * Portal routes, gated on the real partner session (node P1):
 *  - signed out: only the auth screens (invited signup + login).
 *  - signed in: the programs picker, the per-program onboarding wizard, and the
 *    inner app (P2 to P6 fill the inner screens; onboarding routes here after).
 * A phone and an iPad open a folder, the password and the programs list as
 * screens of their own; a computer keeps them inside their pages.
 */
export function App() {
  useTrackHistory();
  const native = useNative();
  const { session, loading } = usePartnerAuth();

  if (loading) {
    return (
      <div className="portal-splash">
        <div className="ps-dot" aria-hidden="true" />
        <span>Loading your portal...</span>
      </div>
    );
  }

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<LoginView />} />
        <Route path="/signup" element={<SignupView />} />
        <Route path="/join/:slug" element={<SignupView />} />
        {/* Why: on a phone the one who opens the app signed out is most often a partner coming back, as after Log out. */}
        <Route path="*" element={<Navigate to={native ? "/login" : "/signup"} replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/programs" replace />} />
      <Route path="/signup" element={<Navigate to="/programs" replace />} />
      <Route path="/join/:slug" element={<Navigate to="/programs" replace />} />
      <Route path="/programs" element={<ProgramsView />} />
      <Route path="/onboarding/:programId" element={<OnboardingView />} />
      <Route element={<PortalShell />}>
        <Route path="/home" element={<HomeView />} />
        <Route path="/referrals" element={<ReferralsView />} />
        <Route path="/assets/:folderId?" element={<AssetsView />} />
        <Route path="/payouts" element={<PayoutsView />} />
        <Route path="/profile" element={<ProfileView />} />
        <Route path="/profile/password" element={native ? <ProfileView /> : <Navigate to="/profile" replace />} />
        <Route path="/profile/programs" element={native ? <ProgramsNative fromAccount /> : <Navigate to="/programs" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/programs" replace />} />
    </Routes>
  );
}
